import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Loader2 } from 'lucide-react';

import { useClients } from '@/lib/clients-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/lib/utils';

const PAGE_SIZE = 10;

/**
 * /clients — listado paginado de clientes del tenant actual.
 * RLS del backend garantiza que sólo se devuelven los clientes cuya
 * tenant_id coincide con el JWT; aquí la UI solo renderiza lo que recibe.
 */
export function Clients() {
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const q = useClients({ page, pageSize: PAGE_SIZE, search: search.trim() || undefined });

  const totalPages = q.data ? Math.max(1, Math.ceil(q.data.total / q.data.pageSize)) : 1;

  return (
    <section className="mx-auto max-w-6xl px-6 py-10 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold tracking-tight">Clients</h1>
          <div className="flex items-center gap-3">
            <Link to="/clients/new">
              <Button size="sm">
                <Plus className="size-4 mr-1" /> New intake
              </Button>
            </Link>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              className="pl-9"
            />
          </div>
        </div>

        <div className="rounded-lg border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>City, State</TableHead>
                <TableHead>Intake</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {q.isLoading && (
                <TableRow>
                  <TableCell colSpan={6} className="py-6 text-muted-foreground text-center">
                    <Loader2 className="inline size-4 animate-spin mr-2" />
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {q.isError && (
                <TableRow>
                  <TableCell colSpan={6} className="py-6 text-destructive text-center">
                    Failed to load clients.
                  </TableCell>
                </TableRow>
              )}
              {q.data && q.data.items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-muted-foreground text-center">
                    No clients yet. Click <strong>New intake</strong> to add your first one.
                  </TableCell>
                </TableRow>
              )}
              {q.data?.items.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <Link to={`/clients/${c.id}`} className="font-medium hover:underline">
                      {c.fullName}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{c.email ?? '—'}</TableCell>
                  <TableCell className="text-muted-foreground">{c.phone ?? '—'}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {c.city}, {c.state}
                  </TableCell>
                  <TableCell>
                    <Badge variant={c.intakeCompleted ? 'default' : 'muted'}>
                      {c.intakeCompleted ? 'completed' : 'pending'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{formatDate(c.createdAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {q.data && (
          <div className="flex items-center justify-between text-sm">
            <p className="text-muted-foreground">
              {q.data.total} client{q.data.total === 1 ? '' : 's'} · page {page + 1} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
    </section>
  );
}